import type { CartAgreementShareEmail } from './cart-agreement.models';
import type { CartHubspotContactSnapshot } from './cart-contact.models';

export type SavedCartStage = 'contract' | 'proposal';

/**
 * Which flow a cart is built in (US 102.1) — the API's spelling, with the underscore. The builder's
 * own tab id is {@link CartChannelId}, which hyphenates `add-on`. `change_order` is a cart opened
 * against a placed order; it is edited on its own page and has no tab in the builder.
 */
export type SavedCartType = 'contract' | 'add_on' | 'onsite' | 'change_order';

/**
 * One cart mode the logged-in user may build in (US 102.1), from
 * `GET /carts/contract-type/modes` — Sales, Booth Builds or Client Services, always in that order.
 *
 * A mode is a team role, so which ones come back depend on the caller: Super Administrator and
 * Developer get all three, everyone else — **including Admin** — gets only the roles assigned to
 * them, and possibly none at all.
 */
export interface CartMode {
  /** Sent as `role_id` when creating a cart or switching its mode. Encrypted, so opaque here. */
  role_id: string;
  /** Immutable across role renames, which is what makes it safe to key UI behaviour on. */
  slug: string;
  /** The label for the dropdown. */
  display_name: string;
}

/**
 * US 102.1 — shown when `GET /carts/contract-type/modes` comes back empty. Not an error state: the
 * user simply holds no cart-mode team role, and so cannot create a cart until one is assigned.
 */
export const NO_CART_MODE_MESSAGE = 'No cart mode assigned.';

/**
 * The statuses a cart can no longer be edited in (US 18.16), and how each reads in a sentence.
 * Draft, shared, approved and rejected carts stay editable; a voided cart that has been reactivated
 * has a live status again, so it is not here.
 */
const LOCKED_STATUS_LABEL: Readonly<Partial<Record<SavedCartStatus, string>>> = {
  signed: 'signed',
  converted_to_contract: 'signed',
  voided: 'voided',
  expired: 'expired',
};

/** Why the Cart Creator will not open this cart, or null when it may. */
export function cartLockedMessage(status: string | null | undefined): string | null {
  const label = status ? LOCKED_STATUS_LABEL[status as SavedCartStatus] : undefined;
  return label ? `This cart is ${label} and can no longer be edited.` : null;
}

/**
 * The statuses the API accepts edits on — its `EDITABLE_STATUSES`. A decided sign-off (US 18.13)
 * hands the cart back, so approved and rejected are here; `approval_pending` is not, so the cart
 * cannot change underneath the approver.
 */
export const EDITABLE_CART_STATUSES: ReadonlySet<SavedCartStatus> = new Set([
  'draft',
  'shared_not_signed',
  'approved',
  'rejected',
]);

/**
 * A proposal or contract has gone out to the customer — every status past the never-sent ones
 * (`draft` and the discount-approval trio). The document names its contact, so that stays put.
 */
export function wasCartSent(status: string | null | undefined): boolean {
  return (
    status === 'shared_not_signed' ||
    status === 'signed' ||
    status === 'converted_to_contract' ||
    status === 'expired' ||
    status === 'voided'
  );
}

/**
 * Shared with the customer and not yet signed — the one status `mark-signed` accepts, so the one
 * a signature bypass is offered on (Order Creation; the Saved Carts menu and the Carts list say the
 * same in their own words).
 */
export function isAwaitingSignature(status: string | null | undefined): boolean {
  return status === 'shared_not_signed';
}

/**
 * An order number as it reads after "Order": "ORD-000100" stands as it is — it already carries its
 * prefix — and only a bare number takes a "#" (Order Creation).
 */
export function formatOrderNumber(orderNumber: string | number): string {
  const value = String(orderNumber);
  return /^[A-Z]+-/.test(value) ? value : `#${value}`;
}

/** What a Booth Build order sets going — the demo's next-step line, said once the order exists. */
export const BOOTH_BUILD_NEXT_STEP =
  'Your booth build kicks off — production sees every line item and photo.';

/** Whether the API will accept edits to a cart in this status. */
export function isCartEditable(status: string | null | undefined): boolean {
  return EDITABLE_CART_STATUSES.has(status as SavedCartStatus);
}

/** The Cart & Order Management listing a locked cart is sent back to. */
export const CARTS_LIST_ROUTE = '/order-management/saved-carts';

/**
 * The switch a cart is being moved through, and whether the rep has accepted what it costs.
 *
 * The endpoint carries both of a cart's switches and takes at least one of them: `cartType` is the
 * Contract / Add-On / Onsite flow, `roleId` the Sales / Booth Builds / Client Services mode. Either
 * may be omitted to leave that half alone.
 */
export interface SwitchCartTypeRequest {
  cartType?: SavedCartType;
  /**
   * The cart mode to move to, from {@link CartMode.role_id} (US 102.1). Changing it always clears
   * the cart server-side — a mode change starts a different sale — and takes no `confirm`: the
   * dialog is the frontend's.
   */
  roleId?: string;
  /**
   * The server's safety net for a switch that destroys something — every move into or out of
   * `add_on`, and either direction on an already-shared cart. Sent as a query param, not a body
   * field.
   */
  confirm?: boolean;
}

export type SavedCartStatus =
  | 'draft'
  | 'shared_not_signed'
  | 'signed'
  | 'converted_to_contract'
  | 'expired'
  | 'voided'
  // Discount approval (US 18.13): a never-sent cart waits here while an admin decides.
  | 'approval_pending'
  | 'approved'
  | 'rejected';

export interface SavedCartLabeledOption<T extends string = string> {
  value: T;
  label: string;
}

export type SavedCartStageOption = SavedCartLabeledOption<SavedCartStage>;
export type SavedCartStatusOption = SavedCartLabeledOption<SavedCartStatus>;

/** Monetary or numeric amount as returned by the carts API (string or number). */
export type SavedCartMoneyValue = string | number | null;

export interface SavedCartShow {
  show_id: string;
  show_title: string;
  city_name: string;
  event_date?: string | null;
}

export interface SavedCartRow {
  id: string;
  cart_number: string;
  company_id?: string | null;
  company_name?: string | null;
  sales_rep_name?: string | null;
  created_by_name?: string | null;
  client_name?: string | null;
  client_email?: string | null;
  stage: SavedCartStage;
  status: SavedCartStatus;
  total: string | number;
  shows: SavedCartShow[];
  expiration_date?: string | null;
  created_at: string;
  updated_at: string;
  // Parent-child contract linking (spec 18.1). Optional until the backend populates them.
  /** The main booth contract's cart id; null/absent for a main contract (parent). */
  parent_cart_id?: string | null;
  /**
   * ⚠️ **The listing and this field disagree.** `GET /carts` documents `cart_type` as US 102.1's
   * flow — `contract | add_on | onsite` — but {@link SavedCartsList.cartTypeLabel} reads it as an
   * item type (`booth_build`, `upsell`) and renders it as the parent/child badge for spec 18.1.
   *
   * Left as a loose `string` rather than narrowed to {@link SavedCartType}, because narrowing would
   * only make the compiler agree with a reading the API does not support. The badge is showing
   * "Contract" / "Add On" / "Onsite" today. Resolve which contract is right before typing it.
   */
  cart_type?: string | null;
  /** Populated once this item's order is created. */
  order_number?: string | null;
}

// ── On-Site Drafts (US 54.24 / 54.25) ─────────────────────────────
/**
 * The zone "today" is measured in on the Drafts view. The day belongs to the salesperson, not the
 * server — at 8 PM Pacific it is already tomorrow in UTC — so the window would otherwise cut off
 * a rep's own drafts mid-evening. The API defaults to `EST` when none is sent.
 */
export type CartDraftTimeZone = 'EST' | 'CST' | 'MST' | 'PST' | 'AKST' | 'HST';

/**
 * One row of `GET /carts/drafts`.
 *
 * Not to be confused with a cart whose {@link SavedCartStatus} is `'draft'` — that is every cart
 * nobody has shared yet. A Draft here is narrower: an Onsite cart, built in Sales mode, that a rep
 * explicitly pressed Save as Draft on. A cart merely left open is not one, which is what keeps the
 * count honest.
 */
export interface CartDraftRow {
  /** The cart id — what the Drafts list opens the builder with. */
  id: string;
  /** The rep-facing number, `D-001`, `D-002`, … Stable across re-saves. */
  draft_number: string;
  /**
   * When the cart was **first** saved as a draft. A re-save leaves it alone, so it keeps naming the
   * day the draft belongs to rather than drifting forward.
   */
  drafted_at: string;
  /**
   * Top-level lines on the cart. Booth setup/cleaning fees and bundled included items are excluded,
   * since both hang off the line they belong to rather than standing alone.
   */
  line_count: number;
}

/** What `POST /carts/{id}/save-as-draft` answers with. Idempotent: a re-save returns the same pair. */
export interface CartDraftStamp {
  draft_number: string;
  drafted_at: string;
}

/** Minimal shape the shared quick-actions component needs — satisfied by both row and detail. */
export interface SavedCartActionable {
  id: string;
  status: SavedCartStatus;
  cart_number?: string | null;
  /** Drives cart-vs-proposal wording in the quick-actions menu; both row and detail carry it. */
  stage?: SavedCartStage | null;
}

// ── Cart detail (GET /carts/:id) ──────────────────────────────────
export interface SavedCartDetailLine {
  id?: string | null;
  /** SBE-2808 — the team's note on this line, shown under it; null when there is none. */
  line_note?: string | null;
  item_type?: string | null;
  product_id?: string | null;
  /** The per-show offering this line was bought from — what Step 2's chips are keyed by. */
  show_product_id?: string | null;
  /** Set on a line the server added as part of a package, pointing at the package's own line. */
  parent_cart_item_id?: string | null;
  /**
   * A Booth Build Heading (US 25.3) — the package line, named by the team. `description` is its
   * Heading Name; its included products follow as children (`parent_cart_item_id`).
   */
  is_package_heading?: boolean;
  /**
   * Add-on booth upgrade: the booked booth line (order_items.id) this line's upgrade group replaces.
   * Set on the new booth, its fees and its credit line; such lines change only through the upgrade.
   */
  upgrade_for_order_item_id?: string | null;
  /**
   * Add-on cart: the customer's booked booth (`order_items.id`) this line was bought for — what the
   * pre-send availability check finds its booth offer by (SBE-2826).
   */
  booth_order_item_id?: string | null;
  /** Booth Build: which side the booth this line is for lives on — with `booth_line_id`, a chip. */
  booth_source_type?: 'order' | 'cart' | null;
  /** Booth Build: the booth line id — an order line or a cart line, per `booth_source_type`. */
  booth_line_id?: string | null;
  description?: string | null;
  quantity?: number | null;
  /**
   * The resolved per-unit sales price. It does not move when a rep prices the line — a chair at
   * `unit_price` 200.00 with `custom_unit_price` 160.00 bills at 160.00 — so the charged figure is
   * {@link custom_unit_price} when set, and this otherwise.
   */
  unit_price?: SavedCartMoneyValue;
  /** A rep's manual Sale Price override (US 20.22), per unit. `null` when none is set. */
  custom_unit_price?: SavedCartMoneyValue;
  /**
   * The List Price: the reference/strikethrough figure the discount is computed against. Falls back
   * to {@link unit_price} when nothing is configured, and equals {@link custom_unit_price} once an
   * override is active, so an overridden line shows no markdown.
   */
  list_price?: SavedCartMoneyValue;
  /**
   * The line total the server computed: the charged price × quantity, except on a default-included line
   * where only the quantity above the free bundle is charged (SBE-2512) — and a manual Sale Price
   * override prices the whole quantity even there.
   */
  amount?: SavedCartMoneyValue;
  is_default_included?: boolean | null;
  is_comped?: boolean | null;
  /**
   * A one-off, non-catalog line typed by hand, whose name is {@link description} and whose entered
   * price is {@link unit_price}. With `item_type === 'booth'` it is a Custom Booth (US 20.4);
   * otherwise a Custom Booth Add-On on a Booth Build cart (25.13). Each has its own edit route. Not
   * to be read as "a rep price sits here": that is {@link custom_unit_price}.
   */
  is_custom?: boolean | null;
  /** Units the applied offer granted free; the rest are charged (SBE-2512). */
  coupon_quantity?: number | null;
  /** Units actually charged for — `quantity` less {@link coupon_quantity}. */
  paid_quantity?: number | null;
  /** How the applied offer touched this line, or null when no offer covers it. */
  coupon_role?: 'free' | 'bundle' | 'bogo' | null;
  /** What the applied offer took off this line. */
  coupon_discount?: SavedCartMoneyValue;
}

export interface SavedCartDetailShow {
  show_id?: unknown;
  show_title?: unknown;
  city_name?: unknown;
  event_date?: unknown;
  /** The event's own subtotal; by contract the shows' subtotals sum to `pricing.subtotal`. */
  subtotal?: SavedCartMoneyValue;
  lines?: SavedCartDetailLine[];
}

/**
 * A discount still attached to the cart that no longer applies (US 18.20) — a lapsed coupon, or an
 * expired or missing gift certificate. It adds nothing to the figures, and while any is present the
 * cart cannot be shared, resent or signed (409 `CART_HAS_INVALID_DISCOUNT`).
 */
export interface CartDiscountIssue {
  kind: 'coupon' | 'gift_certificate';
  /** Null when the coupon or voucher no longer exists at all. */
  code: string | null;
  reason: 'deleted' | 'inactive' | 'not_yet_valid' | 'expired' | 'usage_exhausted' | 'missing';
  message: string;
}

/** Send is held back while a discount no longer applies; this names the first one. */
export function invalidDiscountSendMessage(issue: CartDiscountIssue): string {
  const name = issue.code ? `${issue.code} — ` : '';
  return `Remove or replace the discount that no longer applies before sending: ${name}${issue.message}`;
}

export interface SavedCartPricing {
  subtotal?: SavedCartMoneyValue;
  setup_fees?: SavedCartMoneyValue;
  cleaning_fees?: SavedCartMoneyValue;
  /**
   * The booth setup fee an authorised user pinned for this cart, or null when the fee is simply
   * calculated. This — not the amount — is what says whether the field is overridden, since a
   * pinned figure may legitimately equal the calculated one, or be `0`.
   */
  setup_fees_override?: SavedCartMoneyValue;
  /** The pinned booth cleaning fee, or null when it is calculated. */
  cleaning_fees_override?: SavedCartMoneyValue;
  /**
   * True when a setup-fee override is pinned but every booth carrying the fee has been removed.
   * The figure is retained and re-applies as soon as a qualifying booth is added back; nothing is
   * charged in the meantime.
   */
  setup_fees_override_dormant?: boolean | null;
  /** True when a cleaning-fee override is pinned but has no line to charge it on. */
  cleaning_fees_override_dormant?: boolean | null;
  /**
   * The roll-up of every discount deducted — the applied offer and the manual cart discount
   * together. The two fields below attribute it to a source (US 20.25).
   */
  discount?: SavedCartMoneyValue;
  /** The manual cart-level discount in dollars, already included in {@link discount}. */
  cart_discount_amount?: SavedCartMoneyValue;
  /** The applied offer's own contribution, already included in {@link discount}. */
  coupon_discount?: SavedCartMoneyValue;
  tax?: SavedCartMoneyValue;
  total?: SavedCartMoneyValue;
  total_savings?: SavedCartMoneyValue;
  /** Collected so far; zero until the cart is signed. */
  paid_to_date?: SavedCartMoneyValue;
  /** Total less paid to date, never negative — equals the total until signing (US 18.20). */
  balance_due?: SavedCartMoneyValue;
  /** Discounts that no longer apply (US 18.20); empty when every discount is valid. */
  discount_issues?: CartDiscountIssue[] | null;
  /**
   * SBE-2830 — the onsite service charge, already inside {@link total}: the configured percent of
   * the product total after offer and cart discount. `0` while waived.
   */
  onsite_service_charge?: SavedCartMoneyValue;
  /** The rate applied; **null** when the charge does not apply to this cart at all. */
  onsite_service_charge_percent?: SavedCartMoneyValue;
  /** True while Client Services has waived the charge. */
  onsite_service_charge_waived?: boolean | null;
}

export interface SavedCartPaymentEntry {
  id?: string | null;
  sort_order?: number | null;
  amount_value?: SavedCartMoneyValue;
  payment_date?: string | null;
  method?: string | null;
}

export interface SavedCartPaymentPlan {
  id?: string | null;
  plan_label?: string | null;
  price_type?: string | null;
  entries?: SavedCartPaymentEntry[];
  requires_card_authorization?: boolean | null;
  first_payment_date_overridden?: boolean | null;
}

/**
 * The linked set a cart belongs to (US 21.16 / 21.20) — `link_group` on the cart and on each Saved
 * Carts row, `null` when it is not linked. An explicit link an internal user made, distinct from
 * `related_to` (the add-on or Booth Build parent, derived from the cart's lines). Every role sees it;
 * only linking itself is restricted.
 */
export interface CartLinkGroup {
  group_id: string;
  is_linked: boolean;
  /** The OTHER carts in the set, by number. */
  linked_cart_numbers: string[];
  /** Whether the set's unified link has been sent to the customer. */
  shared: boolean;
  sent_at?: string | null;
  version: number;
}

/**
 * Whether the API offers Link Cart (US 21.17). Its action enum carries both `link_cart` and
 * `link_carts`, and the cart reads answer with `link_carts`, so either one counts.
 */
export function offersLinkCart(actions: readonly string[] | null | undefined): boolean {
  return !!actions?.some((action) => action === 'link_cart' || action === 'link_carts');
}

/**
 * "Linked · C-1029, C-1051 · Sent Oct 13, 2026", or `''` for a cart that is not linked — which then
 * shows no status. The sent part appears once the set's unified link has gone out (US 21.18).
 */
export function linkedCartsLabel(group: CartLinkGroup | null | undefined): string {
  if (!group?.is_linked) return '';
  const numbers = group.linked_cart_numbers.filter(Boolean);
  const linked = numbers.length > 0 ? `Linked · ${numbers.join(', ')}` : 'Linked';
  const sent = linkedSentLabel(group);
  return sent ? `${linked} · ${sent}` : linked;
}

/** "Sent Oct 13, 2026" once the set's link went out, "Sent" when the date is unknown, else `''`. */
export function linkedSentLabel(group: CartLinkGroup | null | undefined): string {
  if (!group?.is_linked || !group.shared) return '';
  const sentAt = group.sent_at ? new Date(group.sent_at) : null;
  if (!sentAt || Number.isNaN(sentAt.getTime())) return 'Sent';
  const date = sentAt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `Sent ${date}`;
}

export interface SavedCartDetail {
  id: string;
  cart_number: string;
  /** US 21.16 / 21.20 — the linked set, or null when the cart is not linked. */
  link_group?: CartLinkGroup | null;
  /** The Actions-menu items this user may take on this cart (US 18.10) — e.g. `link_cart`. */
  available_actions?: string[] | null;
  company_id?: unknown;
  company_name?: unknown;
  created_by?: number | null;
  created_by_type?: string | null;
  deal_owner_name?: unknown;
  deal_owner_email?: unknown;
  stage: SavedCartStage;
  status: SavedCartStatus;
  /**
   * Contract / Add-On / Onsite flow (US 102.1), independent of {@link stage}. The builder's own
   * channel tab is restored from this on load — without it a saved Onsite cart reopens on the
   * Contract tab, taking its Save as Draft button with it.
   *
   * Note the spelling: the API says `add_on` where the builder's `CartChannelId` says `add-on`.
   */
  cart_type?: SavedCartType | null;
  /**
   * Encrypted id of the cart-mode role (Sales / Booth Builds / Client Services) the cart is built
   * in. Fixed per cart; changes only through Start Over. Null only on carts predating US 102.1.
   *
   * An id, not a slug, so it cannot be read as a mode without `GET /carts/contract-type/modes`.
   * Nothing resolves it yet — that is US 102.1's job.
   */
  role_id?: string | null;
  currency?: string | null;
  client_first_name?: unknown;
  client_last_name?: unknown;
  client_email?: unknown;
  /** The Step 1 contact snapshot, or null while no contact is attached. */
  hubspot?: CartHubspotContactSnapshot | null;
  /**
   * `true` once a HubSpot deal exists for the cart and a contact is on it: the deal points at that
   * person, so the customer can no longer be swapped (a swap answers 409). Not an origin flag — a
   * portal cart earns its deal at first send and flips then too.
   */
  contact_locked?: boolean | null;
  coupon_code?: unknown;
  /**
   * The gift-certificate voucher applied to this cart, or null when none is.
   *
   * Left loosely typed on purpose: the API documents the field but not its shape, and inventing an
   * interface would present a guess as fact. Narrow it once the contract says what is inside.
   */
  gift_certificate?: Record<string, unknown> | null;
  expiration_date?: unknown;
  /**
   * Graphics Submission Deadline (`YYYY-MM-DD`) for a Booth Build cart (story 25.3); null on every
   * other cart.
   *
   * **Read-only here** — it is set through `PATCH /booth-build`, not through this cart's own update.
   */
  graphics_deadline?: string | null;
  share_token?: unknown;
  share_url?: unknown;
  /**
   * The composed Shared Cart cover-email draft, or null until a rep edits one.
   *
   * The same draft the Agreement Details step reads from `GET /carts/agreement-details/:id`, which
   * is where the builder takes it from today — this is the cart's own copy of it.
   */
  share_email?: CartAgreementShareEmail | null;
  /**
   * When the customer first opened the shared cart or agreement, or null if they never have.
   * Stamped once and then frozen, so it dates the first open rather than the most recent.
   */
  first_opened_at?: string | null;
  /** The shared snapshot's version, bumped every time a confirmed edit re-shares the cart. */
  shared_version?: number;
  special_notes?: unknown;
  internal_notes?: unknown;
  invoice_note?: unknown;
  additional_terms?: unknown;
  /**
   * The customer-visible intro rendered at the top of the proposal document (Booth Build 25.17 /
   * 25.18). Only meaningful while {@link stage} is `proposal`.
   */
  proposal_cover_letter?: string | null;
  /**
   * Whether a proposal Company Logo has been uploaded for this cart. Only a flag — the viewable URL
   * and which source it came from are fetched separately when this is true.
   */
  has_proposal_logo?: boolean;
  shows?: SavedCartDetailShow[];
  pricing?: SavedCartPricing;
  payment_plan?: SavedCartPaymentPlan | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Body for `POST /carts/hubspot/start-cart` — the one call behind both custom HubSpot buttons.
 *
 * Omitting `hubspot_deal_id` is what says the click came from a CONTACT rather than a DEAL: that
 * absence is the only signal separating the two flows, and the API creates the deal in that case.
 * The endpoint rejects unknown keys, so the field must be absent, not present and undefined.
 */
export interface StartCartFromHubspotDto {
  hubspot_contact_id: string;
  hubspot_deal_id?: string;
  stage: SavedCartStage;
  /**
   * The cart mode to build in (US 102.1) — required, though the API ignores it when an existing
   * draft for this HubSpot record is handed back instead of a new cart.
   */
  role_id: string;
}

/** The deal the call bound or created. The id is null only when HubSpot refused to create it. */
export interface StartCartFromHubspotDeal {
  hubspot_deal_id?: string | null;
  /** True when this call created the deal — contact-level entry, or the linked deal was spent. */
  created: boolean;
}

export interface StartCartFromHubspotResult {
  cart: SavedCartDetail;
  /**
   * True when an untouched draft already existed for this HubSpot record and was handed back
   * instead of a new one. Informational, not an error — it is what makes a refresh safe.
   */
  reused: boolean;
  deal: StartCartFromHubspotDeal;
}

/** Generic response for the simple POST cart actions (resend, void, reactivate, …). */
export interface CartActionResponse {
  message?: string;
}

/** The order the signature bypass creates — exactly once, with pending status (US 21.22). */
export interface CartCreatedOrder {
  id: string;
  order_number: string;
  status: string;
  /** 2-dp total string. */
  total: string;
  cart_id: string;
  /** The cart's status after the order was placed — `signed`, and read-only from then on. */
  cart_status?: string;
  /**
   * The official signed agreement a signature bypass attached (US 18.19); null when the cart was
   * marked signed without a file. The URL is short-lived (about an hour) — for viewing, not keeping.
   */
  signed_agreement?: CartSignedAgreementFile | null;
}

export interface CartSignedAgreementFile {
  file_name: string;
  url: string;
}

/** Where a placed order lands — see `ORDER_MANAGEMENT_ROUTES`. */
export const ORDERS_LIST_ROUTE = '/order-management/orders';

/** The cart listing, and where the builder retreats to when it cannot claim a cart. */
export const SAVED_CARTS_LIST_ROUTE = '/order-management/saved-carts';

/** US 21.16 — the cart is emptied in place, so it is the same cart the rep carries on with. */
export const START_OVER_DONE_MESSAGE = 'New cart started.';

/** Refused on anything but a draft of the admin panel's own making; the server names which. */
export const START_OVER_FAILED_MESSAGE = 'Unable to start over. Please try again.';

/**
 * The Send Email menu's two choices (US 21.14), as `POST /carts/:id/share` names them. `action`
 * decides which document goes out and moves the cart's stage to match.
 */
export type CartShareAction = 'send_proposal_only' | 'email_contract_only';

/** Response for POST /carts/:id/mark-signed, which answers 201 with the order it created. */
export interface CartMarkSignedResponse extends CartActionResponse {
  data?: CartCreatedOrder;
}

/** Response for POST /carts/:id/copy-link — the shareable customer portal / review link. */
export interface CartCopyLinkResponse {
  url?: string;
  link?: string;
  message?: string;
}

/** Response for POST /carts/:id/impersonate — establishes the portal session server-side. */
export interface CartImpersonateResponse {
  url?: string;
  message?: string;
}

/** Something the clone left behind, and why (SBE-2830 `CloneResultDto`). */
export interface CartCloneSkippedItem {
  description?: string | null;
  name?: string | null;
  show_title?: string | null;
  template_name?: string | null;
  reason?: string | null;
}

/** Both cart and order clone endpoints return the new draft cart. */
export interface CartCloneResult {
  cart_id: string;
  cart_number: string;
  source_type?: string;
  source_reference?: string;
  /** Product lines omitted from the clone; unavailable event/package lines abort it with 409. */
  skipped?: CartCloneSkippedItem[] | null;
  is_booth_build?: boolean | null;
  /** Booth Build graphics that could not be copied. */
  skipped_graphics?: CartCloneSkippedItem[] | null;
}

/** The 409 a clone answers when a show it would copy is missing data; `errors` names each. */
export const CLONE_MISSING_EVENT_DATA = 'ERROR_MISSING_EVENT_DATA';

/** "{what} — {why}", for a clone note or refusal line. */
export function cloneNoteText(item: CartCloneSkippedItem): string {
  const what = [item.description || item.name || item.template_name, item.show_title]
    .filter(Boolean)
    .join(' · ');
  return [what || 'An item', item.reason].filter(Boolean).join(' — ');
}

/** One of the cart customer's additional portal users (`GET /carts/quick-actions/:id/portal-users`). */
export interface CartPortalUser {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone?: string | null;
  status?: string | null;
  invitation_status?: string | null;
  /** Active / Pending / Disabled / Revoked. */
  display_status?: string | null;
  invited_at?: string | null;
  invitation_accepted_at?: string | null;
  last_login_at?: string | null;
}

/** Response for POST /carts/:id/invite-user. */
export interface CartInviteUserResponse {
  exhibitor_id?: string;
  email?: string;
  company_id?: string;
  invitation_status?: string;
  expires_at?: string;
}

export interface SavedCartListMeta {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
}

export interface SavedCartListResponse {
  success?: boolean;
  message?: string;
  data: SavedCartRow[];
  meta?: SavedCartListMeta;
}

export interface SavedCartListQuery {
  page: number;
  limit: number;
  stage?: SavedCartStage;
  search?: string;
  from_date?: string;
  to_date?: string;
  city_ids?: string[];
  show_years?: number[];
  statuses?: SavedCartStatus[];
}

export interface SavedCartCityOption {
  id: string;
  name: string;
}

export interface SavedCartCityFilterOption {
  value: string;
  label: string;
}

export interface SavedCartFilterOptionsData {
  cities: SavedCartCityOption[];
  show_years: number[];
  statuses: SavedCartStatusOption[];
}

export interface SavedCartFilterOptionsResponse {
  success?: boolean;
  message?: string;
  data: SavedCartFilterOptionsData;
}

export const SAVED_CART_STATUS_OPTIONS: readonly SavedCartStatusOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'shared_not_signed', label: 'Shared Cart - Not Signed Yet' },
  { value: 'signed', label: 'Signed' },
  { value: 'converted_to_contract', label: 'Converted to Contract' },
  { value: 'expired', label: 'Expired' },
  { value: 'voided', label: 'Voided' },
  { value: 'approval_pending', label: 'Approval Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
] as const;

/**
 * Body of PATCH /carts/:id/items/:itemId — the Cart Preview's stepper (US 20.21) and its Sale
 * Price override (US 20.22) share it. Every field is optional; send the one being changed.
 */
export interface UpdateCartItemPayload {
  /** Minimum 1 — the stepper floors there rather than letting the API reject a 0. */
  quantity?: number;
  /**
   * Manual Sale Price override, per unit (US 20.22), gated on `cart.custom-price`.
   *
   * `null` is meaningful: it *clears* the override and returns the line to its catalogue price. So
   * this must never be dropped by a truthiness or `!= null` filter on the way to the request.
   */
  custom_unit_price?: number | null;
  /**
   * The line's List Price — the struck-through reference, apart from the Sale Price above
   * (`cart.edit-list-price`, Booth Builds). There is no clear form: omit it to leave it alone.
   */
  list_price_override?: number;
  /**
   * SBE-2808 — the Booth Build team's note on this line, up to 2000 characters. Client-facing: it
   * travels onto the order. `null` clears it; leave it out to keep it.
   */
  line_note?: string | null;
}

/** Fallback toast when the API refuses a quantity edit without a message of its own. */
export const CART_QUANTITY_UPDATE_FAILED = 'The quantity could not be updated.';

/**
 * Body of PATCH /carts/booth-fees/:id — the figure the customer pays for booth setup and cleaning
 * on this cart, gated on `cart.edit-fees`.
 *
 * Each fee has **three** distinct states, and conflating any two of them loses information:
 * the key **absent** leaves that fee untouched, a number pins it as the final figure, and `null`
 * clears the override so the fee falls back to the calculated one. `0` pins the fee at free — it is
 * a value, not a clear. So callers build this by adding the one key they mean, never by spreading
 * a possibly-`undefined` pair.
 */
export interface UpdateCartFeesDto {
  setup_fees?: number | null;
  cleaning_fees?: number | null;
}

/** Fallback toast when the API refuses a booth-fee override without a message of its own. */
export const CART_FEE_UPDATE_FAILED = 'The fee could not be updated.';

/** Fallback toast when the API refuses a Sale Price override without a message of its own. */
export const CART_PRICE_UPDATE_FAILED = 'The price could not be updated.';

/** Fallback toast when the API refuses a List Price override (SBE-2796) without its own message. */
export const CART_LIST_PRICE_UPDATE_FAILED = 'The list price could not be updated.';

/** Fallback toast when the API refuses a line note (SBE-2808) without its own message. */
export const CART_NOTE_UPDATE_FAILED = 'The note could not be saved.';

/** Clear All's confirmation, as the demo words it (SBE-2808). */
export const CART_CLEARED_MESSAGE = 'Cart cleared';

/** US 20.22 — the override is numeric, non-negative and at most 2 decimal places. */
export const INVALID_PRICE_MESSAGE = 'Enter a price with up to 2 decimal places.';

export const SAVED_CART_STAGE_OPTIONS: readonly SavedCartStageOption[] = [
  { value: 'contract', label: 'Saved Cart' },
  { value: 'proposal', label: 'Proposal' },
] as const;
